import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { staffService } from '../../../services/staffService';

const ConsultationDateTime = ({ bookingData, onDataUpdate }) => {
  const [selectedDate, setSelectedDate] = useState(bookingData?.dateTime?.date || null);
  const [selectedSlot, setSelectedSlot] = useState(bookingData?.dateTime?.timeSlot || null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Generate next 14 days
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date?.setDate(today?.getDate() + i);
      dates?.push(date);
    }
    return dates;
  };

  const availableDates = generateDates();

  // RE-ENABLED - Load available slots when date/doctor changes (SAFE - has proper conditions)
  useEffect(() => {
    if (selectedDate && bookingData?.doctor?.id) {
      loadAvailableSlots();
    }
  }, [selectedDate, bookingData?.doctor?.id]);

  // RE-ENABLED - Update parent when date/slot changes (SAFE - only updates when values change)
  useEffect(() => {
    onDataUpdate({
      dateTime: {
        date: selectedDate,
        timeSlot: selectedSlot
      }
    });
  }, [selectedDate, selectedSlot]);

  const loadAvailableSlots = async () => {
    try {
      setLoading(true);
      setError('');
      // Use consultation-specific availability checking (excludes therapy appointments)
      const slots = await staffService?.getAvailableSlotsForConsultation(
        bookingData?.doctor?.id,
        selectedDate
      );
      setAvailableSlots(slots || []);

      if (slots?.length === 0) {
        setError('This day is a weekly off or no slots are available. Please select another date.');
      } else if (slots?.every(s => !s?.available)) {
        setError('This day is a weekly off or all slots are fully booked. Please select another date.');
      }
    } catch (err) {
      setError('Failed to load available slots');
      console.error('Error loading slots:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if a date is a weekly off (no available slots)
  const isWeeklyOff = (dateStr) => {
    // This will be determined after loading slots
    // For now, we'll check if it's the selected date and has no available slots
    if (selectedDate === dateStr) {
      return availableSlots?.length > 0 && availableSlots?.every(s => !s?.available);
    }
    return false;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDayName = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const isToday = (date) => {
    const today = new Date();
    const compareDate = new Date(date);
    return (today?.getDate() === compareDate?.getDate() &&
      today?.getMonth() === compareDate?.getMonth() && today?.getFullYear() === compareDate?.getFullYear());
  };

  const formatTimeSlot = (time) => {
    if (!time) return '';
    const [hours, minutes] = time?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const isPastSlot = (slotTime) => {
    if (!selectedDate || !slotTime) return false;

    const now = new Date();
    const today = new Date(now?.getFullYear(), now?.getMonth(), now?.getDate());
    const slotDate = new Date(selectedDate);

    // Only filter past slots for today
    if (slotDate?.getTime() !== today?.getTime()) {
      return false;
    }

    const currentTime = now?.toTimeString()?.slice(0, 5);
    return slotTime <= currentTime;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-purple-800 mb-2">Select Date & Time</h2>
        <p className="text-gray-600">Choose your preferred consultation slot</p>
      </div>
      {!bookingData?.doctor && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Please select a doctor first to view available slots</p>
        </div>
      )}
      {bookingData?.doctor && (
        <>
          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <CalendarIcon className="w-4 h-4 inline mr-2" />
              Select Date
            </label>
            <div className="grid grid-cols-7 gap-2">
              {availableDates?.map((date, index) => {
                const dateStr = date?.toISOString()?.split('T')?.[0];
                const isSelected = selectedDate === dateStr;
                const isOff = isWeeklyOff(dateStr);

                return (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedDate(dateStr);
                      setSelectedSlot(null);
                    }}
                    className={`p-3 rounded-lg border-2 transition-all relative ${isSelected
                      ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
                      }`}
                  >
                    <div className="text-xs text-gray-600">{getDayName(date)}</div>
                    <div className={`text-lg font-semibold ${isSelected ? 'text-purple-600' : 'text-gray-800'
                      }`}>
                      {date?.getDate()}
                    </div>
                    {isToday(date) && (
                      <div className="text-xs text-purple-600">Today</div>
                    )}
                    {isOff && isSelected && (
                      <div className="text-xs mt-1 bg-red-500 text-white px-1 py-0.5 rounded">
                        Weekly Off
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Slot Selection */}
          {selectedDate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Clock className="w-4 h-4 inline mr-2" />
                Available Time Slots for {formatDate(selectedDate)}
              </label>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : error && availableSlots?.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">{error}</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {availableSlots?.map((slot, index) => {
                    // Use the flags from the backend
                    const isPast = slot?.isPast || false;
                    const isBooked = slot?.isBooked || false;
                    const isAvailable = slot?.available && !isPast && !isBooked;

                    // Determine slot status
                    let statusLabel = '';
                    let statusColor = '';
                    if (isPast) {
                      statusLabel = 'Past';
                      statusColor = 'text-gray-400';
                    } else if (isBooked) {
                      statusLabel = `Booked (${slot?.bookingCount}/${slot?.maxBookings})`;
                      statusColor = 'text-orange-500';
                    }

                    return (
                      <button
                        key={index}
                        onClick={() => isAvailable && setSelectedSlot(slot?.time)}
                        disabled={!isAvailable}
                        className={`p-3 rounded-lg border-2 transition-all ${selectedSlot === slot?.time
                          ? 'border-purple-600 bg-purple-50 text-purple-600'
                          : isAvailable
                            ? 'border-gray-300 hover:border-purple-400'
                            : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                          }`}
                        title={
                          isPast ? 'This time slot has passed' :
                            isBooked ? `Fully booked (${slot?.bookingCount}/${slot?.maxBookings})` :
                              isAvailable ? `${slot?.bookingCount || 0}/${slot?.maxBookings || 1} slots booked` :
                                'Not available'
                        }
                      >
                        <div className="text-sm font-semibold">
                          {formatTimeSlot(slot?.time)}
                        </div>
                        {(isPast || isBooked) && (
                          <div className={`text-xs mt-1 font-medium ${statusColor}`}>
                            {statusLabel}
                          </div>
                        )}
                        {isAvailable && slot?.bookingCount > 0 && (
                          <div className="text-xs mt-1 text-green-600 font-medium">
                            {slot?.maxBookings - slot?.bookingCount} left
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {selectedDate && selectedSlot && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="font-semibold text-purple-800 mb-1">Selected Consultation Time</p>
              <p className="text-gray-700">
                {formatDate(selectedDate)} at {formatTimeSlot(selectedSlot)}
              </p>
            </div>
          )}
        </>
      )}
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-purple-600 bg-purple-50 rounded"></div>
          <span>Selected</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-gray-300 rounded"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-gray-200 bg-gray-100 rounded opacity-50"></div>
          <span>Booked</span>
        </div>
      </div>
    </div>
  );
};

export default ConsultationDateTime;